import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Settings, ChevronDown, ChevronUp, Printer, FileText } from 'lucide-react';
import ScheduleCPrint from './ScheduleCPrint';

// Schedule C line items matching IRS form exactly
const SCHEDULE_C_INCOME_LINES = [
  { line: '1', description: 'Gross receipts or sales' },
  { line: '2', description: 'Returns and allowances' },
  { line: '6', description: 'Other income, including federal and state gasoline or fuel tax credit or refund' },
];

const SCHEDULE_C_EXPENSE_LINES_LEFT = [
  { line: '8', description: 'Advertising' },
  { line: '9', description: 'Car and truck expenses' },
  { line: '10', description: 'Commissions and fees' },
  { line: '11', description: 'Contract labor' },
  { line: '12', description: 'Depletion' },
  { line: '13', description: 'Depreciation and section 179\nexpense deduction' },
  { line: '14', description: 'Employee benefit programs' },
  { line: '15', description: 'Insurance (other than health)' },
  { line: '16', description: 'Interest (see instructions):', hasSublines: true, sublines: [
    { line: '16a', description: 'Mortgage (paid to banks, etc.)' },
    { line: '16b', description: 'Other' }
  ]},
  { line: '17', description: 'Legal and professional services' },
];

const SCHEDULE_C_EXPENSE_LINES_RIGHT = [
  { line: '18', description: 'Office expense' },
  { line: '19', description: 'Pension and profit-sharing plans' },
  { line: '20', description: 'Rent or lease (see instructions):', hasSublines: true, sublines: [
    { line: '20a', description: 'Vehicles, machinery, and equipment' },
    { line: '20b', description: 'Other business property' }
  ]},
  { line: '21', description: 'Repairs and maintenance' },
  { line: '22', description: 'Supplies' },
  { line: '23', description: 'Taxes and licenses' },
  { line: '24', description: 'Travel and meals:', hasSublines: true, sublines: [
    { line: '24a', description: 'Travel' },
    { line: '24b', description: 'Deductible meals' }
  ]},
  { line: '25', description: 'Utilities' },
  { line: '26', description: 'Wages (less employment credits)' },
  { line: '27a', description: 'Other expenses (from line 48)', isCalculated: true },
];

export default function ScheduleC({ user, taxYear }) {
  const [mappings, setMappings] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseVendors, setExpenseVendors] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [scheduleCTotals, setScheduleCTotals] = useState({});
  const [taxDeposits, setTaxDeposits] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [showScheduleC, setShowScheduleC] = useState(false);
  const [newMapping, setNewMapping] = useState({
    category_type: 'expense',
    category_name: '',
    schedule_c_line: '',
    schedule_c_description: ''
  });

  const isOwner = user?.role === 'owner';

  // State for IRS-style print view
  const [showIRSPrint, setShowIRSPrint] = useState(false);

  // Helper function to get total for a line (including sublines)
  const getLineTotal = (line) => {
    if (line.hasSublines && line.sublines) {
      return line.sublines.reduce((sum, subline) => sum + (scheduleCTotals[subline.line] || 0), 0);
    }
    return scheduleCTotals[line.line] || 0;
  };

  // Add a refresh key that changes when we need to force refresh
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Get date range for the selected tax year
  const getTaxYearDateRange = () => {
    const year = parseInt(taxYear) || new Date().getFullYear();
    return {
      start: `${year}-01-01`,
      end: `${year}-12-31`
    };
  };
  
  // Fetch data on mount, when panel opens, taxYear changes, or when refreshKey changes
  useEffect(() => {
    if (showScheduleC) {
      fetchMappings();
      fetchDropdownData();
      fetchScheduleCReport();
      fetchTaxDeposits();
      fetchSummary();
    }
  }, [showScheduleC, refreshKey, taxYear]);

  // Also expose a refresh function for external triggers
  useEffect(() => {
    const handleRefresh = () => {
      setRefreshKey(prev => prev + 1);
    };
    window.addEventListener('scheduleC:refresh', handleRefresh);
    return () => window.removeEventListener('scheduleC:refresh', handleRefresh);
  }, []);

  const fetchTaxDeposits = async () => {
    try {
      const range = getTaxYearDateRange();
      const response = await axios.get(`${API}/tax-deposits?start_date=${range.start}&end_date=${range.end}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTaxDeposits(response.data);
    } catch (error) {
      console.error('Failed to fetch tax deposits:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const range = getTaxYearDateRange();
      const response = await axios.get(`${API}/summary?start_date=${range.start}&end_date=${range.end}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchMappings = async () => {
    try {
      const response = await axios.get(`${API}/schedule-c-mappings?_t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setMappings(response.data);
    } catch (error) {
      console.error('Failed to fetch mappings', error);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [sourcesRes, incomeCatRes, vendorsRes, expenseCatRes] = await Promise.all([
        axios.get(`${API}/income-sources`),
        axios.get(`${API}/income-categories`),
        axios.get(`${API}/expense-vendors`),
        axios.get(`${API}/expense-categories`)
      ]);
      
      setIncomeSources(sourcesRes.data);
      setIncomeCategories(incomeCatRes.data);
      setExpenseVendors(vendorsRes.data);
      setExpenseCategories(expenseCatRes.data);
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  };

  const fetchScheduleCReport = async () => {
    try {
      const range = getTaxYearDateRange();
      const response = await axios.get(`${API}/schedule-c-report?start_date=${range.start}&end_date=${range.end}&_t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setScheduleCTotals(response.data.totals || {});
    } catch (error) {
      console.error('Failed to fetch Schedule C report', error);
    }
  };

  const handleAddMapping = async () => {
    console.log('Attempting to add mapping:', newMapping);
    
    // Validate all required fields
    if (!newMapping.category_name || !newMapping.category_name.trim()) {
      toast.error('Please select a category');
      return;
    }
    
    if (!newMapping.schedule_c_line || !newMapping.schedule_c_line.trim()) {
      toast.error('Please select a Schedule C line');
      return;
    }

    if (!newMapping.schedule_c_description || !newMapping.schedule_c_description.trim()) {
      toast.error('Schedule C line description is missing');
      return;
    }

    try {
      console.log('Sending mapping request:', newMapping);
      const response = await axios.post(`${API}/schedule-c-mappings`, newMapping);
      console.log('Mapping created successfully:', response.data);
      
      toast.success(`Mapping created: ${newMapping.category_name} → Line ${newMapping.schedule_c_line}`);
      setShowAddMapping(false);
      setNewMapping({
        category_type: 'expense',
        category_name: '',
        schedule_c_line: '',
        schedule_c_description: ''
      });
      await fetchMappings();
      await fetchScheduleCReport();
    } catch (error) {
      console.error('Error adding mapping:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || `Failed to add mapping: ${error.message}`);
    }
  };

  const handleDeleteMapping = async (categoryType, categoryName) => {
    if (!window.confirm('Delete this mapping?')) return;

    try {
      await axios.delete(`${API}/schedule-c-mappings/${categoryType}/${categoryName}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Mapping deleted');
      await Promise.all([fetchMappings(), fetchScheduleCReport()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete mapping');
    }
  };

  const handleClearAllMappings = async () => {
    if (!window.confirm('Delete ALL Schedule C mappings? This cannot be undone and you will need to recreate all mappings.')) return;
    
    try {
      await axios.delete(`${API}/schedule-c-mappings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('All mappings deleted successfully');
      await Promise.all([fetchMappings(), fetchScheduleCReport()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete mappings');
    }
  };

  const getAvailableCategories = () => {
    if (newMapping.category_type === 'income') {
      return incomeCategories.map(c => ({ name: c.name, label: c.name }));
    } else {
      return expenseCategories.map(c => ({ name: c.name, label: c.name }));
    }
  };

  const getScheduleCLines = () => {
    return newMapping.category_type === 'income' 
      ? SCHEDULE_C_INCOME_LINES 
      : [...SCHEDULE_C_EXPENSE_LINES_LEFT, ...SCHEDULE_C_EXPENSE_LINES_RIGHT];
  };

  const formatAmount = (amount) => {
    return `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handlePrint = async () => {
    // First, expand the Schedule C panel if it's collapsed
    if (!showScheduleC) {
      setShowScheduleC(true);
      // Wait for data to load before printing
      setTimeout(() => {
        performPrint();
      }, 500);
    } else {
      // Refresh data before printing to ensure we have latest mappings
      await fetchMappings();
      await fetchDropdownData();
      performPrint();
    }
  };

  const performPrint = () => {
    // Get all unique categories from income and expenses
    const mappedIncomeCategories = new Set(
      mappings.filter(m => m.category_type === 'income').map(m => m.category_name)
    );
    const mappedExpenseCategories = new Set(
      mappings.filter(m => m.category_type === 'expense').map(m => m.category_name)
    );

    // Check if all income SOURCES (types of income) are mapped, not categories (customers)
    const unmappedIncomeCategories = incomeSources.filter(
      source => !mappedIncomeCategories.has(source.name)
    );

    // Check if all expense categories are mapped (excluding Tax Deposits)
    const unmappedExpenseCategories = expenseCategories.filter(
      cat => !mappedExpenseCategories.has(cat.name) && cat.name !== 'Tax Deposits'
    );

    const totalUnmapped = unmappedIncomeCategories.length + unmappedExpenseCategories.length;

    if (totalUnmapped > 0) {
      let message = '⚠️ Warning: Not all categories are mapped to Schedule C lines!\n\n';
      
      if (unmappedIncomeCategories.length > 0) {
        message += 'Unmapped Income Categories:\n';
        unmappedIncomeCategories.forEach(cat => {
          message += `• ${cat.name}\n`;
        });
        message += '\n';
      }
      
      if (unmappedExpenseCategories.length > 0) {
        message += 'Unmapped Expense Categories:\n';
        unmappedExpenseCategories.forEach(cat => {
          message += `• ${cat.name}\n`;
        });
        message += '\n';
      }
      
      message += 'Unmapped categories will not appear on the printed form.\n\nDo you want to continue printing anyway?';
      
      if (!window.confirm(message)) {
        return;
      }
    }

    // Trigger browser print
    window.print();
  };

  // Group mappings by Schedule C line
  const groupedMappings = mappings.reduce((acc, mapping) => {
    const line = mapping.schedule_c_line;
    if (!acc[line]) {
      acc[line] = [];
    }
    acc[line].push(mapping);
    return acc;
  }, {});

  return (
    <>
    {/* IRS Form Print Modal - rendered OUTSIDE schedule-c-panel to avoid CSS overrides */}
    {showIRSPrint && (
      <ScheduleCPrint
        user={user}
        taxYear={taxYear || new Date().getFullYear().toString()}
        onClose={() => setShowIRSPrint(false)}
      />
    )}
    <div className="space-y-0 bg-white border-2 md:border-4 border-black p-0 font-serif" data-testid="schedule-c-panel" style={{fontFamily: 'Times New Roman, serif'}}>
      {/* IRS Form Header */}
      <div className="border-b-2 border-black p-2 md:p-4">
        <div className="flex justify-between items-start">
          <div 
            className="flex-1 cursor-pointer hover:bg-slate-50 transition-colors p-1 md:p-2 -m-1 md:-m-2 rounded"
            onClick={() => setShowScheduleC(!showScheduleC)}
          >
            <div className="flex items-center gap-2 md:gap-3">
              <div className="flex-1">
                <div className="text-[10px] md:text-xs font-bold">SCHEDULE C</div>
                <div className="text-[10px] md:text-xs">(Form 1040)</div>
                <div className="text-sm md:text-lg font-bold mt-1">Profit or Loss From Business</div>
                <div className="text-[10px] md:text-xs mt-1">(Sole Proprietorship)</div>
                <div className="hidden md:block text-[10px] mt-2">▶ Go to www.irs.gov/ScheduleC for instructions and the latest information.</div>
                <div className="hidden md:block text-[10px]">▶ Attach to Form 1040, 1040-SR, 1040-SS, 1040-NR, or 1041; partnerships must generally file Form 1065.</div>
              </div>
              <div className="ml-2 md:ml-4">
                {showScheduleC ? (
                  <ChevronUp className="w-5 h-5 md:w-6 md:h-6 text-slate-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 md:w-6 md:h-6 text-slate-600" />
                )}
              </div>
            </div>
          </div>
          <div className="text-right ml-2 md:ml-4 flex flex-col items-end">
            <div className="flex gap-1 mb-1 md:mb-2">
              <Button
                onClick={() => setShowIRSPrint(true)}
                variant="outline"
                size="sm"
                className="gap-1 print:hidden text-xs px-2 h-7 md:h-9 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                data-testid="print-irs-form"
                title="Print IRS Form"
              >
                <FileText className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">IRS Form</span>
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline"
                size="sm"
                className="gap-1 print:hidden text-xs px-2 h-7 md:h-9"
                data-testid="print-schedule-c"
                title="Print This View"
              >
                <Printer className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Print</span>
              </Button>
            </div>
            <div className="text-[8px] md:text-[10px] font-bold">OMB No. 1545-0074</div>
            <div className="text-lg md:text-2xl font-bold mt-1">{taxYear || new Date().getFullYear()}</div>
            <div className="text-[8px] md:text-[10px] mt-1">Attachment</div>
            <div className="text-[8px] md:text-[10px]">Sequence No. 09</div>
          </div>
        </div>
      </div>

      {/* Schedule C Content - Only show when expanded */}
      {showScheduleC && (
        <>
          {/* Part I: Income - IRS Form Style */}
          <div className="border-b-2 border-black">
        <div className="bg-black text-white px-4 py-1 text-sm font-bold">
          Part I Income
        </div>
        
        <div className="p-4 space-y-2">
          {SCHEDULE_C_INCOME_LINES.map((line, idx) => {
            const lineMappings = groupedMappings[line.line] || [];
            const total = scheduleCTotals[line.line] || 0;
            
            return (
              <div key={line.line} className="flex items-start border-b border-slate-200 pb-2">
                <div className="w-8 text-xs font-bold">{line.line}</div>
                <div className="flex-1">
                  <div className="text-xs">{line.description}</div>
                  {lineMappings.length > 0 && (
                    <div className="mt-1 text-[10px] text-blue-600 mapped-categories">
                      Mapped: {lineMappings.map(m => m.category_name).join(', ')}
                    </div>
                  )}
                </div>
                <div className="w-32 text-right">
                  <div className="border border-black px-2 py-1 text-sm font-mono bg-white">
                    {formatAmount(total)}
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Calculated lines */}
          <div className="flex items-start border-b border-slate-200 pb-2">
            <div className="w-8 text-xs font-bold">3</div>
            <div className="flex-1 text-xs">Subtract line 2 from line 1</div>
            <div className="w-32 text-right">
              <div className="border border-black px-2 py-1 text-sm font-mono bg-white">
                {formatAmount((scheduleCTotals['1'] || 0) - (scheduleCTotals['2'] || 0))}
              </div>
            </div>
          </div>
          
          <div className="flex items-start border-b border-slate-200 pb-2">
            <div className="w-8 text-xs font-bold">4</div>
            <div className="flex-1 text-xs">Cost of goods sold (from line 42)</div>
            <div className="w-32 text-right">
              <div className="border border-black px-2 py-1 text-sm font-mono bg-white">
                {formatAmount(0)}
              </div>
            </div>
          </div>
          
          <div className="flex items-start border-b border-slate-200 pb-2">
            <div className="w-8 text-xs font-bold">5</div>
            <div className="flex-1 text-xs font-bold">Gross profit. Subtract line 4 from line 3</div>
            <div className="w-32 text-right">
              <div className="border-2 border-black px-2 py-1 text-sm font-mono bg-yellow-50 font-bold">
                {formatAmount((scheduleCTotals['1'] || 0) - (scheduleCTotals['2'] || 0))}
              </div>
            </div>
          </div>
          
          <div className="flex items-start border-b-2 border-black pb-2">
            <div className="w-8 text-xs font-bold">7</div>
            <div className="flex-1 text-xs font-bold">Gross income. Add lines 5 and 6</div>
            <div className="w-32 text-right">
              <div className="border-2 border-black px-2 py-1 text-sm font-mono bg-yellow-50 font-bold">
                {formatAmount(
                  (scheduleCTotals['1'] || 0) - (scheduleCTotals['2'] || 0) + (scheduleCTotals['6'] || 0)
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Part II: Expenses - IRS Form Style with Two Columns */}
      <div className="border-b-2 border-black">
        <div className="bg-black text-white px-4 py-1 text-sm font-bold">
          Part II Expenses. Enter expenses for business use of your home only on line 30.
        </div>
        
        <div className="grid grid-cols-2 gap-0">
          {/* Left Column - Lines 8-17 */}
          <div className="p-4 space-y-2 border-r border-black">
            {SCHEDULE_C_EXPENSE_LINES_LEFT.map((line) => {
              // Special handling for lines with sublines (like line 16)
              if (line.hasSublines && line.sublines) {
                return (
                  <div key={line.line} className="border-b border-slate-200 pb-2">
                    {/* Main line header */}
                    <div className="flex items-start">
                      <div className="w-8 text-xs font-bold whitespace-nowrap">{line.line}</div>
                      <div className="flex-1">
                        <div className="text-xs">{line.description}</div>
                      </div>
                      <div className="w-24 text-right ml-2">
                        {/* No amount on the main line */}
                      </div>
                    </div>
                    {/* Sublines */}
                    {line.sublines.map((subline) => {
                      const sublineMappings = groupedMappings[subline.line] || [];
                      const subtotal = scheduleCTotals[subline.line] || 0;
                      
                      return (
                        <div key={subline.line} className="flex items-start mt-1">
                          <div className="w-8 text-xs font-bold whitespace-nowrap pl-2">{subline.line}</div>
                          <div className="flex-1">
                            <div className="text-xs">{subline.description}</div>
                            {sublineMappings.length > 0 && (
                              <div className="mt-1 text-[10px] text-blue-600 mapped-categories">
                                {sublineMappings.map(m => m.category_name).join(', ')}
                              </div>
                            )}
                          </div>
                          <div className="w-24 text-right ml-2">
                            <div className="border border-black px-1 py-1 text-xs font-mono bg-white">
                              {formatAmount(subtotal)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              
              // Regular line without sublines
              const lineMappings = groupedMappings[line.line] || [];
              const total = scheduleCTotals[line.line] || 0;
              
              return (
                <div key={line.line} className="flex items-start border-b border-slate-200 pb-2">
                  <div className="w-8 text-xs font-bold whitespace-nowrap">{line.line}</div>
                  <div className="flex-1">
                    <div className="text-xs whitespace-pre-line">{line.description}</div>
                    {lineMappings.length > 0 && (
                      <div className="mt-1 text-[10px] text-blue-600 mapped-categories">
                        {lineMappings.map(m => m.category_name).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="w-24 text-right ml-2">
                    <div className="border border-black px-1 py-1 text-xs font-mono bg-white">
                      {formatAmount(total)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Right Column - Lines 18-27a */}
          <div className="p-4 space-y-2">
            {SCHEDULE_C_EXPENSE_LINES_RIGHT.map((line) => {
              // Special handling for lines with sublines (like line 24)
              if (line.hasSublines && line.sublines) {
                return (
                  <div key={line.line} className="border-b border-slate-200 pb-2">
                    {/* Main line header */}
                    <div className="flex items-start">
                      <div className="w-8 text-xs font-bold whitespace-nowrap">{line.line}</div>
                      <div className="flex-1">
                        <div className="text-xs">{line.description}</div>
                      </div>
                      <div className="w-24 text-right ml-2">
                        {/* No amount on the main line */}
                      </div>
                    </div>
                    {/* Sublines */}
                    {line.sublines.map((subline) => {
                      const sublineMappings = groupedMappings[subline.line] || [];
                      const subtotal = scheduleCTotals[subline.line] || 0;
                      
                      return (
                        <div key={subline.line} className="flex items-start mt-1">
                          <div className="w-8 text-xs font-bold whitespace-nowrap pl-2">{subline.line}</div>
                          <div className="flex-1">
                            <div className="text-xs">{subline.description}</div>
                            {sublineMappings.length > 0 && (
                              <div className="mt-1 text-[10px] text-blue-600 mapped-categories">
                                {sublineMappings.map(m => m.category_name).join(', ')}
                              </div>
                            )}
                          </div>
                          <div className="w-24 text-right ml-2">
                            <div className="border border-black px-1 py-1 text-xs font-mono bg-white">
                              {formatAmount(subtotal)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              
              // Regular lines without sublines
              const lineMappings = groupedMappings[line.line] || [];
              const total = scheduleCTotals[line.line] || 0;
              
              return (
                <div key={line.line} className="flex items-start border-b border-slate-200 pb-2">
                  <div className="w-8 text-xs font-bold whitespace-nowrap">{line.line}</div>
                  <div className="flex-1">
                    <div className="text-xs whitespace-pre-line">{line.description}</div>
                    {/* Only show mappings if this is not a calculated line */}
                    {!line.isCalculated && lineMappings.length > 0 && (
                      <div className="mt-1 text-[10px] text-blue-600 mapped-categories">
                        {lineMappings.map(m => m.category_name).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="w-24 text-right ml-2">
                    <div className="border border-black px-1 py-1 text-xs font-mono bg-white">
                      {formatAmount(total)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Total Expenses and Net Profit */}
        <div className="p-4 border-t-2 border-black space-y-2">
          <div className="flex items-start border-b border-slate-200 pb-2">
            <div className="w-8 text-xs font-bold">28</div>
            <div className="flex-1 text-xs font-bold">Total expenses before expenses for business use of home. Add lines 8 through 27a</div>
            <div className="w-32 text-right">
              <div className="border-2 border-black px-2 py-1 text-sm font-mono bg-yellow-50 font-bold">
                {formatAmount(
                  [...SCHEDULE_C_EXPENSE_LINES_LEFT, ...SCHEDULE_C_EXPENSE_LINES_RIGHT]
                    .reduce((sum, line) => sum + getLineTotal(line), 0)
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-start border-b-2 border-black pb-2">
            <div className="w-8 text-xs font-bold">29</div>
            <div className="flex-1 text-xs font-bold">Tentative profit or (loss). Subtract line 28 from line 7</div>
            <div className="w-32 text-right">
              <div className="border-2 border-black px-2 py-1 text-sm font-mono bg-yellow-50 font-bold">
                {formatAmount(
                  ((scheduleCTotals['1'] || 0) - (scheduleCTotals['2'] || 0) + (scheduleCTotals['6'] || 0)) -
                  [...SCHEDULE_C_EXPENSE_LINES_LEFT, ...SCHEDULE_C_EXPENSE_LINES_RIGHT]
                    .reduce((sum, line) => sum + getLineTotal(line), 0)
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-start border-b-2 border-black pb-2 pt-2">
            <div className="w-8 text-xs font-bold">31</div>
            <div className="flex-1 text-xs font-bold">Net profit or (loss). Subtract line 30 from line 29</div>
            <div className="w-32 text-right">
              <div className="border-4 border-black px-2 py-2 text-base font-mono bg-green-50 font-bold">
                {formatAmount(
                  ((scheduleCTotals['1'] || 0) - (scheduleCTotals['2'] || 0) + (scheduleCTotals['6'] || 0)) -
                  [...SCHEDULE_C_EXPENSE_LINES_LEFT, ...SCHEDULE_C_EXPENSE_LINES_RIGHT]
                    .reduce((sum, line) => sum + getLineTotal(line), 0)
                )}
              </div>
            </div>
          </div>

          {/* Tax Summary Section - hidden in print */}
          {taxDeposits && (
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg print-hide-tax-summary">
              <div className="text-sm font-bold text-purple-900 mb-3">Tax Payment Summary</div>
              {(() => {
                // Calculate net profit
                const grossIncome = (scheduleCTotals['1'] || 0) - (scheduleCTotals['2'] || 0) + (scheduleCTotals['6'] || 0);
                const totalExpenses = [...SCHEDULE_C_EXPENSE_LINES_LEFT, ...SCHEDULE_C_EXPENSE_LINES_RIGHT]
                  .reduce((sum, line) => sum + getLineTotal(line), 0);
                const netProfit = grossIncome - totalExpenses;
                
                // Estimated tax is 20% of POSITIVE profit only - no tax on losses
                const estimatedTax = Math.max(0, netProfit * 0.20);
                
                // Balance = estimated tax - deposits (negative means refund)
                const balance = estimatedTax - taxDeposits.total_deposits;
                
                return (
                  <>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div className="bg-white/50 p-2 rounded">
                        <div className="text-slate-600 mb-1">Estimated Tax (20%)</div>
                        <div className="text-lg font-bold text-purple-700">
                          {formatAmount(estimatedTax)}
                        </div>
                      </div>
                      <div className="bg-white/50 p-2 rounded">
                        <div className="text-slate-600 mb-1">Tax Deposits Made</div>
                        <div className="text-lg font-bold text-emerald-700">
                          {formatAmount(taxDeposits.total_deposits)}
                        </div>
                        <div className="text-[10px] text-slate-500">{taxDeposits.deposit_count} payment{taxDeposits.deposit_count !== 1 ? 's' : ''}</div>
                      </div>
                      <div className="bg-white/50 p-2 rounded">
                        <div className="text-slate-600 mb-1">
                          {balance > 0 ? 'Balance Owed' : 'Refund Due'}
                        </div>
                        <div className={`text-lg font-bold ${balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                          {formatAmount(Math.abs(balance))}
                        </div>
                      </div>
                    </div>
                    {balance <= 0 && taxDeposits.total_deposits > 0 && (
                      <div className="mt-2 text-xs text-green-700 bg-green-100 p-2 rounded">
                        ✓ Tax liability fully paid!
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
        </>
      )}

    </div>
    </>
  );
}
